/**
 * Tags query hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tagsApi, holdingTagsApi, watchlistTagsApi } from '../api'
import type { TagCreate, TagUpdate } from '../types'
import { HOLDINGS_KEY } from './useHoldings'
import { WATCHLIST_KEY } from './useWatchlist'

export const TAGS_KEY = ['tags']

export function useTags() {
  return useQuery({
    queryKey: TAGS_KEY,
    queryFn: () => tagsApi.list(),
  })
}

export function useTag(tagId: string) {
  return useQuery({
    queryKey: [...TAGS_KEY, tagId],
    queryFn: () => tagsApi.get(tagId),
    enabled: !!tagId,
  })
}

export function useTagHoldings(tagId: string) {
  return useQuery({
    queryKey: [...TAGS_KEY, tagId, 'holdings'],
    queryFn: () => tagsApi.getHoldings(tagId),
    enabled: !!tagId,
  })
}

export function useTagWatchlist(tagId: string) {
  return useQuery({
    queryKey: [...TAGS_KEY, tagId, 'watchlist'],
    queryFn: () => tagsApi.getWatchlist(tagId),
    enabled: !!tagId,
  })
}

export function useCreateTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: TagCreate) => tagsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAGS_KEY })
    },
  })
}

export function useUpdateTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ tagId, data }: { tagId: string; data: TagUpdate }) =>
      tagsApi.update(tagId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAGS_KEY })
    },
  })
}

export function useDeleteTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (tagId: string) => tagsApi.delete(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TAGS_KEY })
      // Also invalidate holdings and watchlist since they may have had this tag
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
      queryClient.invalidateQueries({ queryKey: WATCHLIST_KEY })
    },
  })
}

// Hooks for setting tags on holdings/watchlist
export function useSetHoldingTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ holdingId, tagIds }: { holdingId: string; tagIds: string[] }) =>
      holdingTagsApi.set(holdingId, tagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HOLDINGS_KEY })
      queryClient.invalidateQueries({ queryKey: TAGS_KEY })
    },
  })
}

export function useSetWatchlistTags() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ symbol, tagIds }: { symbol: string; tagIds: string[] }) =>
      watchlistTagsApi.set(symbol, tagIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WATCHLIST_KEY })
      queryClient.invalidateQueries({ queryKey: TAGS_KEY })
    },
  })
}
